import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, IconButton, Typography, Button, TextField, Tooltip, Snackbar, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Close as CloseIcon, Preview as PreviewIcon, PersonAdd as PersonAddIcon, Download as DownloadIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import ModalCcAsistenciasYNota from './ModalCcAsistenciasYNota.jsx';
import ModalConsultarCcCuil from './ModalConsultarCcCuil.jsx';
import { confirmarCcAsistencia, getInscriptosByEvento } from '../../services/cc_asistencia.service.js';
import dayjs from 'dayjs';

export default function ModalListaCcParticipantes({ open, onClose, nombreCurso, fechaEvento, idEvento, cupo, onDataChange }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [filterText, setFilterText] = useState('');
    const [inscriptos, setInscriptos] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchInscriptos = async (silent = false) => {
        if (!idEvento) return;
        if (!silent) setLoading(true);
        try {
            const data = await getInscriptosByEvento(idEvento);
            setInscriptos(data);
        } catch (err) {
            console.error("Error fetching inscriptos:", err);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (open && idEvento) {
            fetchInscriptos();
            const interval = setInterval(() => {
                fetchInscriptos(true);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [open, idEvento]);

    // Rows parsing
    const rows = (inscriptos || []).map((p, idx) => ({ id: p.id || idx, ...p }));

    const filteredRows = rows.filter(row => {
        const lower = filterText.toLowerCase();
        return (
            (row.cuil && row.cuil.toLowerCase().includes(lower)) ||
            (row.nombre && row.nombre.toLowerCase().includes(lower)) ||
            (row.apellido && row.apellido.toLowerCase().includes(lower))
        );
    });

    const [openNotaModal, setOpenNotaModal] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [openConsultarCuil, setOpenConsultarCuil] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmOverLimit, setConfirmOverLimit] = useState(false);
    const [pendingParticipant, setPendingParticipant] = useState(null);

    const handleOpenNotaModal = (row) => {
        setSelectedParticipant(row);
        setOpenNotaModal(true);
    };

    const handleConfirmNewParticipant = async (userData) => {
        // Calcular presentes actuales
        const presentCount = (inscriptos || []).filter(p => Number(p.estado_asistencia) === 1).length;

        // Validar cupo
        if (cupo > 0 && presentCount >= cupo) {
            setPendingParticipant(userData);
            setConfirmOverLimit(true);
            return;
        }

        executeManualRegistration(userData);
    };

    const executeManualRegistration = async (userData) => {
        try {
            const result = await confirmarCcAsistencia({ cuil: userData.cuil, evento_id: idEvento, origen: 'manual' });

            if (result && result.code === 'ALREADY_REGISTERED') {
                setSnackbar({ open: true, message: 'Este participante ya tiene registrada su asistencia.', severity: 'info' });
            } else {
                setSnackbar({ open: true, message: `Se agregó a ${userData.nombre} ${userData.apellido} correctamente`, severity: 'success' });
            }

            if (onDataChange) onDataChange();
            fetchInscriptos(true); // Actualizar lista local inmediatamente tras registro manual
        } catch (error) {
            setSnackbar({ open: true, message: 'Error al agregar participante', severity: 'error' });
        } finally {
            setOpenConsultarCuil(false);
            setConfirmOverLimit(false);
            setPendingParticipant(null);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Asistencia');

            worksheet.columns = [
                { header: 'CUIL', key: 'cuil', width: 20 },
                { header: 'Apellido', key: 'apellido', width: 30 },
                { header: 'Nombres', key: 'nombre', width: 30 },
                { header: 'Celular', key: 'celular', width: 20 },
                { header: 'Correo Electrónico', key: 'correo', width: 35 },
                { header: 'Fecha/Hora Inscripción', key: 'fecha_hora_inscripcion', width: 25 },
                { header: '¿Es Empleado?', key: 'es_empleado', width: 18 },
                { header: 'Id Empleado', key: 'id_empleado', width: 18 },
                { header: 'Nota', key: 'nota', width: 10 },
                { header: 'Estado Asistencia', key: 'estado_asistencia', width: 20 },
            ];

            (inscriptos || []).forEach(p => {
                const fechaInscripcionFormatted = p.fecha_hora_inscripcion && dayjs(p.fecha_hora_inscripcion).isValid()
                    ? dayjs(p.fecha_hora_inscripcion).format('DD/MM/YYYY HH:mm:ss')
                    : (p.fecha_hora_inscripcion || 'No registrada');

                worksheet.addRow({
                    cuil: p.cuil,
                    apellido: p.apellido,
                    nombre: p.nombre,
                    celular: p.celular || 'No tiene',
                    correo: p.correo || 'No tiene',
                    fecha_hora_inscripcion: fechaInscripcionFormatted,
                    es_empleado: Number(p.es_empleado) === 1 ? 'Si' : 'No',
                    id_empleado: p.id_empleado || 'No tiene',
                    nota: p.nota,
                    estado_asistencia: Number(p.estado_asistencia) === 1 ? 'Presente' : 'Ausente'
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const safeCursoName = nombreCurso?.replace(/[^a-z0-9]/gi, '_') || 'Curso';
            link.download = `Asistentes_${safeCursoName}_${fechaEvento}.xlsx`;
            link.click();
        } catch (err) {
            console.error('Error Excel download:', err);
        }
    };

    const columns = [
        { field: 'cuil', headerName: 'CUIL', minWidth: 130, flex: 0.7 },
        { field: 'apellido', headerName: 'Apellido', minWidth: 160, flex: 1 },
        { field: 'nombre', headerName: 'Nombres', minWidth: 160, flex: 1 },
        { field: 'celular', headerName: 'Celular', minWidth: 140, flex: 0.8, valueFormatter: (value) => value || '-' },
        { field: 'correo', headerName: 'Correo Electrónico', minWidth: 220, flex: 1.2 },
        {
            field: 'fecha_hora_inscripcion',
            headerName: 'Fecha/Hora Inscripción',
            minWidth: 180,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => {
                if (!value) return '-';
                const d = dayjs(value);
                return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : value;
            }
        },
        {
            // El backend ya entrega la fecha en formato DD/MM/AAAA
            field: 'fecha_nacimiento',
            headerName: 'Fecha de Nacimiento',
            minWidth: 150,
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => value || '-',
            sortComparator: (a, b) => {
                // Ordenar cronológicamente (DD/MM/AAAA no ordena bien como texto)
                const toSortable = (v) => (v ? v.split('/').reverse().join('') : '');
                return toSortable(a).localeCompare(toSortable(b));
            }
        },
        { field: 'nota', headerName: 'Nota', width: 90, align: 'center', headerAlign: 'center', valueFormatter: value => value != null ? value : '-' },
        { field: 'estado_asistencia', headerName: 'Asistencia', width: 110, align: 'center', headerAlign: 'center', valueFormatter: value => Number(value) === 1 ? 'Presente' : 'Ausente' },
        {
            field: 'acciones', headerName: 'Acciones', width: 90, align: 'center', sortable: false, renderCell: (params) => (
                <Tooltip title="Ver detalle" slotProps={{ tooltip: { sx: { fontFamily: 'Poppins' } } }}>
                    <IconButton aria-label="Ver detalle asistente" size="medium" color="primary" onClick={() => handleOpenNotaModal(params.row)}>
                        <PreviewIcon />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: '90vh',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 3, pb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A' }}>
                        Inscriptos
                    </Typography>
                    <Typography variant="body" sx={{ color: '#5C6F82', fontFamily: 'Poppins', mt: 0.5 }}>
                        {nombreCurso} {fechaEvento ? ` | ${fechaEvento.split('-').reverse().join('-')}` : ''}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ color: '#9E9E9E', '&:hover': { color: '#1A1A1A', bgcolor: 'rgba(0,0,0,0.04)' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box sx={{ px: 3, display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
                <TextField
                    placeholder="Filtrar por CUIL, nombre o apellido..."
                    variant="outlined"
                    size="small"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    InputProps={{
                        sx: {
                            fontFamily: 'Poppins',
                            borderRadius: '8px',
                            bgcolor: '#fff',
                            fontSize: '0.95rem'
                        }
                    }}
                    sx={{ width: { xs: '100%', md: 400 } }}
                />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadExcel}
                        sx={{
                            fontFamily: 'Geogrotesque Sharp',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            py: 1,
                            px: 2,
                            fontWeight: 'bold'
                        }}
                    >
                        DESCARGAR LISTA
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setOpenConsultarCuil(true)}
                        sx={{
                            fontFamily: 'Geogrotesque Sharp',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            py: 1,
                            px: 2,
                            fontWeight: 'bold',
                            boxShadow: '0 4px 12px rgba(0,123,255,0.1)'
                        }}
                    >
                        AGREGAR PARTICIPANTE
                    </Button>
                </Box>
            </Box>

            <DialogContent dividers sx={{ p: 0, px: 3, pb: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1, width: '100%', height: 600 }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        loading={loading}
                        rowHeight={70}
                        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                        disableSelectionOnClick
                        sx={{
                            border: 'none',
                            fontFamily: 'Poppins',
                            fontSize: '0.95rem',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#F8FAFC',
                                color: '#64748B',
                                fontWeight: 600,
                                borderBottom: '1px solid #E2E8F0'
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #F1F5F9',
                                display: 'flex',
                                alignItems: 'center'
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: '#F8FAFC'
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: 'none'
                            }
                        }}
                    />
                </Box>
            </DialogContent>

            {/* Modals */}
            {openNotaModal && selectedParticipant && (
                <ModalCcAsistenciasYNota
                    open={openNotaModal}
                    onClose={() => setOpenNotaModal(false)}
                    participante={selectedParticipant}
                    idEvento={idEvento}
                    nombreCurso={nombreCurso}
                    onDataChange={onDataChange}
                />
            )}

            {openConsultarCuil && (
                <ModalConsultarCcCuil
                    open={openConsultarCuil}
                    onClose={() => setOpenConsultarCuil(false)}
                    idEvento={idEvento}
                    nombreCurso={nombreCurso}
                    onConfirmAttendance={handleConfirmNewParticipant}
                />
            )}

            <Dialog
                open={confirmOverLimit}
                onClose={() => setConfirmOverLimit(false)}
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold' }}>Cupo Superado</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Poppins' }}>
                        El cupo establecido ha sido superado. ¿Desea registrar igualmente la asistencia de este participante?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfirmOverLimit(false)} sx={{ fontFamily: 'Geogrotesque Sharp' }}>CANCELAR</Button>
                    <Button
                        onClick={() => executeManualRegistration(pendingParticipant)}
                        variant="contained"
                        color="primary"
                        sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', borderRadius: '8px' }}
                    >
                        SÍ, REGISTRAR
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px' }}>{snackbar.message}</Alert>
            </Snackbar>

        </Dialog>
    );
}
